import { AuctionTable } from '../problems/AuctionTable';
import type { AuctionBid } from '../../types/problems';
import { formatBidToken } from '../../utils/formatBidToken';

interface Props {
  fullAuction: AuctionBid[];
  jeffSeq: number;
  vulnerability: string;
  decisionSeat: 'N' | 'E' | 'S' | 'W';
}

/**
 * FR-019a wrapper around the shared AuctionTable for tournament-hand items on the
 * Overcalling Data Editing page. It:
 *
 * (a) Replaces the bid at Jeff's `jeffSeq` slot with a "?" token, rendered
 *     plainly by the shared `BidCell` (black "?", `text-black`) EXACTLY as every
 *     other page shows Jeff's decision point — problem-side auctions already
 *     encode "?" at Jeff's seat (Appendix A.6) and render through the same path.
 *     Session 2026-07-06: the former solid brand-blue "?" badge (white "?" on
 *     bg-primary-600, pill-shaped) was removed at the editors' request so the
 *     Data Editing / Assign Difficulty auction looks the same as the publicly
 *     accessible practice pages.
 * (b) Truncates the auction at Jeff's `jeffSeq` position so the "?" is the LAST
 *     cell shown, matching the public practice-page view (FR-019a(b), Session
 *     2026-07-06 — reverses the Session 2026-05-20 full-auction form). Calls
 *     after `jeffSeq` (later opponent/partner bids, Jeff's own later bids, or the
 *     three closing passes) are not rendered. The shared AuctionTable pads the
 *     trailing seats of the final row with blank cells and emits no rows beneath,
 *     so the public-page look falls out of a pure array-slice.
 * (d) Normalizes every non-masked bid to display form via the shared
 *     `formatBidToken` helper — raw `cardsgamehands.biddingsequence.bid` tokens
 *     `P` / `X` / `XX` render as `Pass` / `Dbl` / `Rdbl`, matching the FR-025
 *     "Jeff's bid" panel and the problem-side auction convention (Appendix A.6).
 *
 * OCP-compliant: the shared `AuctionTable` and `BidCell` components are NOT
 * modified. This wrapper only substitutes "?" + normalized tokens into the
 * auction array before passing it to `AuctionTable`; there is no post-render DOM
 * mutation. (The Session 2026-05-28 brand-blue badge and its Session 2026-05-21
 * per-navigation reset — former sub-clause (c) — were both removed with the badge
 * on 2026-07-06, since a plainly-rendered "?" carries no styling that could bleed
 * across Previous/Next navigation.) The Overcalling Practice page (008) and other
 * problem / hand displays are unaffected.
 *
 * Masking rules:
 * - Only the slot at index `jeffSeq - 1` is masked. Earlier Jeff passes stay
 *   visible as their (normalized) actual values per (d); later Jeff bids are
 *   truncated away per (b).
 * - Applies in ALL DS categories (overcall and pass categories alike) — the "?"
 *   always marks "this is Jeff's decision", regardless of whether his actual bid
 *   was an overcall, a pass, a double, or any other call.
 */
export function OvercallingAuctionDisplay({
  fullAuction,
  jeffSeq,
  vulnerability,
  decisionSeat,
}: Props) {
  // FR-019a(b) (Session 2026-07-06): truncate at Jeff's decision point so the "?"
  // is the last cell, matching the public practice-page view. Slice so the masked
  // slot at index jeffSeq-1 is the last element; a malformed jeffSeq (< 1 or
  // beyond the auction length) falls back to the full sequence so the auction
  // never throws or renders blank.
  const truncatedAuction: AuctionBid[] =
    jeffSeq >= 1 && jeffSeq <= fullAuction.length
      ? fullAuction.slice(0, jeffSeq)
      : fullAuction;

  // FR-019a (a) + (d): replace the jeff_seq slot's call with '?' (masked, and its
  // alert nulled so it can't leak the curated call), and normalize every other
  // call via formatBidToken so raw P / X / XX render as Pass / Dbl / Rdbl. The
  // '?' is then rendered plainly by BidCell (text-black) — no badge, no DOM walk.
  const maskedAuction: AuctionBid[] = truncatedAuction.map((bid, idx) => {
    if (jeffSeq >= 1 && idx === jeffSeq - 1) {
      // FR-009: hide the masked bid's alert along with its call — an alert often
      // describes the bid's meaning and would leak the call the editor is curating.
      return { ...bid, call: '?', alert: null };
    }
    return { ...bid, call: formatBidToken(bid.call) };
  });

  return (
    <div
      className="overcalling-auction-display"
      data-testid="overcalling-auction-display"
    >
      <AuctionTable
        auction={maskedAuction}
        vulnerability={vulnerability}
        decisionSeat={decisionSeat}
      />
    </div>
  );
}
